/**
 * Analyzer: Day Mistakes
 *
 * Surfaces the day's dominant mistake and quantifies its cost. Mirrors the
 * legacy `generateDailyReview`'s "Discipline Focus" block but emits as an
 * Observation so the Coach pipeline can rank it against other signals.
 *
 * Also emits a "clean execution" success when trades >= 3 and no mistakes
 * were recorded — rewards flawless execution days.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayMistakes';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day) return null;

  const { mistakes, core } = day;
  if (!mistakes || !core) return null;

  // Celebrate clean execution on days with enough sample size
  if (mistakes.tradesWithMistakes === 0 && core.tradesCount >= 3) {
    return makeObservation({
      id: `${key}:${day.date}:clean`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.DISCIPLINE,
      title: 'Clean execution — zero mistakes',
      detail: `All ${core.tradesCount} trades executed without a recorded mistake.`,
      action: 'What pre-trade routine carried you today? Write it down so you can repeat it.',
      evidence: { tradesCount: core.tradesCount },
      confidence: 0.9,
      priority: 60
    });
  }

  if (mistakes.tradesWithMistakes === 0) return null;

  // Top mistake + cost
  const sorted = Object.entries(mistakes.counts).sort((a, b) => b[1] - a[1]);
  if (sorted.length === 0) return null;

  const [topName, topCount] = sorted[0];
  const mistakeRatio = core.tradesCount > 0 ? mistakes.tradesWithMistakes / core.tradesCount : 0;
  const runnerUp = sorted[1] ? ` "${sorted[1][0]}" followed with ${sorted[1][1]}.` : '';

  const costClause = mistakes.mistakePnL < 0
    ? ` Cost today: $${Math.abs(mistakes.mistakePnL).toFixed(0)}.`
    : (mistakes.cleanPnL > mistakes.mistakePnL && mistakes.cleanPnL > 0
        ? ` Clean trades produced $${mistakes.cleanPnL.toFixed(0)} vs $${mistakes.mistakePnL.toFixed(0)} with mistakes.`
        : '');

  // Severity tilts with both frequency and financial pain
  const severity = (mistakeRatio >= 0.5 || mistakes.mistakePnL < -100)
    ? SEVERITY.WARNING
    : SEVERITY.INFO;

  return makeObservation({
    id: `${key}:${day.date}:${topName}`,
    analyzer: key,
    scope: 'day',
    scopeId: day.date,
    severity,
    category: CATEGORY.DISCIPLINE,
    title: `"${topName}" dominated today's mistakes`,
    detail: `${topCount}× across ${mistakes.tradesWithMistakes} of ${core.tradesCount} trades.${runnerUp}${costClause}`,
    action: `Add a one-line rule to your pre-trade checklist that would have blocked "${topName}".`,
    evidence: {
      topMistake: topName,
      topCount,
      allCounts: mistakes.counts,
      mistakePnL: mistakes.mistakePnL,
      cleanPnL: mistakes.cleanPnL,
      mistakeRatio
    },
    metric: { label: 'Frequency', value: topCount, baseline: core.tradesCount, unit: 'of' },
    confidence: Math.min(0.95, 0.6 + mistakeRatio * 0.4),
    priority: 85
  });
};
